import fs from 'fs/promises';
import path from 'path';

import type { Logger } from 'pino';

import type { CompiledRulesBundle, WalkerStats } from '../types';
import {
  shouldIncludeFile,
  shouldPruneDirectory,
} from '../rules/RulesCompiler';
import type { FilesRepo } from '../db/FilesRepo';

import { toPosixRelative } from './PathUtil';

const EMPTY_STATS: WalkerStats = {
  processed: 0,
  skipped: 0,
  dirtied: 0,
  missing: 0,
};

function cloneStats(stats: WalkerStats): WalkerStats {
  return { ...stats };
}

export interface InventoryOptions {
  rootPath: string;
  bundle: CompiledRulesBundle;
  filesRepo: FilesRepo;
  logger: Logger;
}

async function readSymlinkTarget(
  absPath: string,
  log: Logger,
): Promise<string | null> {
  try {
    return await fs.readlink(absPath);
  } catch (error: any) {
    log.warn({ err: error, path: absPath }, 'Failed to read symlink target');
    return null;
  }
}

async function walkDirectory(
  current: string,
  opts: InventoryOptions,
  stats: WalkerStats,
): Promise<void> {
  const dirLogger = opts.logger;
  let dirents;
  try {
    dirents = await fs.readdir(current, { withFileTypes: true });
  } catch (error: any) {
    dirLogger.warn({ err: error, path: current }, 'Failed to read directory');
    return;
  }

  for (const dirent of dirents) {
    const absPath = path.join(current, dirent.name);
    const relPath = toPosixRelative(opts.rootPath, absPath);

    if (dirent.isDirectory()) {
      if (shouldPruneDirectory(relPath, opts.bundle)) {
        stats.skipped += 1;
        continue;
      }
      await walkDirectory(absPath, opts, stats);
      continue;
    }

    if (dirent.isSymbolicLink() || dirent.isFile()) {
      let stat;
      try {
        stat = await fs.lstat(absPath);
      } catch (error: any) {
        dirLogger.warn({ err: error, path: absPath }, 'Failed to stat file');
        continue;
      }

      stats.processed += 1;
      if (!shouldIncludeFile(relPath, stat, opts.bundle)) {
        stats.skipped += 1;
        continue;
      }

      // Skip zero-length files (empty files cause server delays)
      if (stat.size === 0) {
        stats.skipped += 1;
        dirLogger.debug({ relPath }, 'Skipping zero-length file');
        continue;
      }

      const target = dirent.isSymbolicLink()
        ? await readSymlinkTarget(absPath, dirLogger)
        : null;
      const state = opts.filesRepo.upsertFromStat({
        relPath,
        displayPath: absPath,
        stats: stat,
        isSymlink: dirent.isSymbolicLink(),
        symlinkTarget: target,
      });
      if (state === 'dirty') {
        stats.dirtied += 1;
      }
      continue;
    }

    // Non-regular file types (sockets, FIFOs, etc.)
    stats.skipped += 1;
  }
}

export async function runInventory(
  opts: InventoryOptions,
): Promise<WalkerStats> {
  const stats = cloneStats(EMPTY_STATS);
  const startedAt = Date.now();
  await walkDirectory(opts.rootPath, opts, stats);
  const missing = opts.filesRepo.markMissingBefore(startedAt);
  stats.missing = missing;
  opts.logger.info({ ...stats }, 'Completed initial inventory');
  return stats;
}
